import Phaser from 'phaser';
import { getEntityScale } from '../constants/scale';
import { PUDDLES_BY_ENEMY, PUDDLE_ALPHA } from '../constants/puddles';

// Декаль-лужи на земле от смерти зайца. W13-фича: накапливаются по матчам, но
// живут только в пределах вкладки браузера — на reload страницы (или новой сессии)
// массив пустой. Намеренно НЕ персистим: переполнение CloudStorage/localStorage,
// и от перезапуска вкладки «свежий лес» приятнее, чем 300 луж со старта.
//
// Хранилище: module-level массив. При смерти зайца → recordRabbitPuddle(scene, x, y)
// добавляет PuddleData + рендерит с pop-in анимацией. При старте новой GameScene →
// renderAllPuddles(scene) перерисовывает накопленные (без pop-in, сразу видны),
// потому что Phaser-объекты gibли вместе со старой сценой при scene.restart.

// Список ключей текстур луж — в constants/puddles.ts (легковесный модуль без
// зависимостей, чтобы BootScene не тянул цепочку scale → platform → Player
// и не ломал порядок инициализации модулей).

// Мягкий лимит: при превышении убираем самые старые (FIFO). 300 — баланс между
// «визуально что-то накопилось» и «не сажает GPU на 100 одновременных Image'ах».
const PUDDLE_LIMIT = 300;

// Depth ниже игрока/врагов (default 0) и леса (10), чтобы спрайты живых
// существ были сверху. Без коллизий — Image, не Sprite, не arcade body.
const PUDDLE_DEPTH = -5;

// Базовый множитель размера лужи от config.size врага, та же K-логика что
// у SPRITE_HEIGHT_MULTIPLIER в Enemy.ts. Лужа примерно того же диаметра, что
// и анимированный спрайт умершего врага (а не фиксированного «зайца»).
const PUDDLE_HEIGHT_MULTIPLIER = 2.0;

// Pop-in анимация смерти: scale 0.6 → final, alpha 0 → 1 за POP_DURATION_MS.
const POP_DURATION_MS = 150;
const POP_START_SCALE = 0.6;
const POP_START_ALPHA = 0;

interface PuddleData {
  textureKey: string;
  x: number;
  y: number;
  rotation: number;
  randomScale: number;
  // Базовый размер врага на момент смерти — чтобы перерисовка после рестарта
  // сцены давала ту же визуальную высоту лужи, что и при первом спавне.
  baseSize: number;
}

const puddles: PuddleData[] = [];

// Карта PuddleData → Image. Привязана к текущей живой сцене (при restart старые
// Image'ы уже destroyed, ссылки протухли). При renderAllPuddles очищаем и
// рендерим заново — это безопасно, потому что старые объекты уже мёртвые.
const rendered = new Map<PuddleData, Phaser.GameObjects.Image>();

export function recordEnemyPuddle(
  enemyId: string,
  scene: Phaser.Scene,
  x: number,
  y: number,
  baseSize: number,
): void {
  // Нет массива текстур для этого id → лужи не спавним. Это намеренно: при
  // подключении нового спрайтового врага можно НЕ добавлять лужи (например,
  // боссам), пропуская их в PUDDLES_BY_ENEMY.
  const variants = PUDDLES_BY_ENEMY[enemyId];
  if (!variants || variants.length === 0) return;
  const textureKey = variants[Phaser.Math.Between(0, variants.length - 1)];
  const data: PuddleData = {
    textureKey,
    x,
    y,
    rotation: 0,
    randomScale: Phaser.Math.FloatBetween(0.85, 1.15),
    baseSize,
  };
  puddles.push(data);
  // FIFO: при переполнении выкидываем самую старую + уничтожаем её Image
  // (если она сейчас отрендерена в этой сцене).
  while (puddles.length > PUDDLE_LIMIT) {
    const oldest = puddles.shift();
    if (oldest) {
      rendered.get(oldest)?.destroy();
      rendered.delete(oldest);
    }
  }
  renderPuddle(scene, data, true);
}

// Вызывается из GameScene.create() — перерисовывает все накопленные лужи в
// новой сцене (старые Image'ы протухли вместе со старой сценой при scene.restart).
export function renderAllPuddles(scene: Phaser.Scene): void {
  // rendered хранит ссылки на УЖЕ DESTROYED объекты прошлой сцены — чистим
  // mapping. Новые объекты создадим ниже с привязкой к текущей сцене.
  rendered.clear();
  for (const data of puddles) {
    renderPuddle(scene, data, false);
  }
}

function renderPuddle(scene: Phaser.Scene, data: PuddleData, animate: boolean): void {
  if (!scene.textures.exists(data.textureKey)) return;
  const image = scene.add
    .image(data.x, data.y, data.textureKey)
    .setDepth(PUDDLE_DEPTH)
    .setRotation(data.rotation);

  // Целевая высота как у спрайта врага (config.size × entityScale × K), ширина —
  // из aspect текстуры. Поверх — рандомный множитель ×0.85..1.15 для разнообразия.
  const targetH = data.baseSize * getEntityScale(scene) * PUDDLE_HEIGHT_MULTIPLIER;
  const aspect = image.width / image.height;
  image.setDisplaySize(targetH * aspect, targetH);
  const baseScale = image.scaleX;
  const finalScale = baseScale * data.randomScale;

  if (animate) {
    image.setScale(finalScale * POP_START_SCALE);
    image.setAlpha(POP_START_ALPHA);
    scene.tweens.add({
      targets: image,
      scaleX: finalScale,
      scaleY: finalScale,
      alpha: PUDDLE_ALPHA,
      duration: POP_DURATION_MS,
      ease: 'Cubic.easeOut',
    });
  } else {
    image.setScale(finalScale);
    image.setAlpha(PUDDLE_ALPHA);
  }

  rendered.set(data, image);
}
